// src/components/features/PreActivitySelector/usePreActivitySelector.js
// Hook encapsulating data fetching and UI logic for the Activity Sequence Builder.
// It exposes sorted object types and activities, multi-select state, and drag/drop handlers.
import { useState, useEffect, useMemo } from "react";
import { activitySequenceApi } from "../api/activitySequenceApi";

export function usePreActivitySelector() {
  const [objectTypes, setObjectTypes] = useState([]);
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [activities, setActivities] = useState([]); // All activities for the selected object type (alphabetically sorted)
  const [selectedActivities, setSelectedActivities] = useState([]); // User-selected subset

  // sequence is an array of slots, each slot is an array of activity names (parallel support)
  const [sequence, setSequence] = useState([]);

  const [dragged, setDragged] = useState({ activity: null, fromSlot: null });
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmedSequence, setConfirmedSequence] = useState(null);
  const [saveResponse, setSaveResponse] = useState(null); // response from backend
  const [error, setError] = useState(null);

  // Load object types on mount
  useEffect(() => {
    async function fetchObjectTypes() {
      try {
        const types = await activitySequenceApi.getObjectTypes();
        // Defensive alphabetical sort in UI (backend also returns sorted)
        const sorted = (types || []).slice().sort((a, b) => a.localeCompare(b));
        setObjectTypes(sorted);
      } catch (err) {
        setError("Failed to load object types");
      }
    }
    fetchObjectTypes();
  }, []);

  // Load activities when selectedObjectType changes
  useEffect(() => {
    if (!selectedObjectType) {
      // Reset all state when object type is cleared
      setActivities([]);
      setSelectedActivities([]);
      setSequence([]);
      setIsConfirmed(false);
      setConfirmedSequence(null);
      setSaveResponse(null);
      setError(null);
      return;
    }

    async function fetchActivities() {
      try {
        const acts = await activitySequenceApi.getActivitiesForObjectType(selectedObjectType);
        // Defensive alphabetical sort in UI
        const sortedActs = (acts || []).slice().sort((a, b) => a.localeCompare(b));
        setActivities(sortedActs);
        // Reset selections for new object type
        setSelectedActivities([]);
        setSequence([]);
        setIsConfirmed(false);
        setConfirmedSequence(null);
        setSaveResponse(null);
        setError(null);
      } catch (err) {
        setError("Failed to load activities");
      }
    }
    fetchActivities();
  }, [selectedObjectType]);

  // When selectedActivities changes, adjust sequence slots count to match length
  useEffect(() => {
    const slotCount = selectedActivities.length;
    setSequence((prev) => {
      // Normalize to array of arrays
      const normalized = Array.isArray(prev) ? prev.map((s) => (Array.isArray(s) ? s : []) ) : [];
      // Ensure activities in sequence are only from selectedActivities
      const filtered = normalized.map((slot) => slot.filter((a) => selectedActivities.includes(a)));
      // Truncate or extend slots to match slotCount
      if (filtered.length > slotCount) return filtered.slice(0, slotCount);
      if (filtered.length < slotCount) {
        return [...filtered, ...Array.from({ length: slotCount - filtered.length }, () => [])];
      }
      return filtered;
    });
    setIsConfirmed(false);
    setConfirmedSequence(null);
    setSaveResponse(null);
  }, [selectedActivities]);

  // Derived: activities not yet placed in any slot (for Area 1)
  const placedSet = useMemo(() => new Set(sequence.flat()), [sequence]);
  const availableTiles = useMemo(
    () => selectedActivities.filter((a) => !placedSet.has(a)),
    [selectedActivities, placedSet]
  );

  // Handlers
  const handleObjectTypeChange = (e) => {
    setSelectedObjectType(e.target.value);
  };

  const handleActivitiesChange = (e) => {
    // Multi-select dropdown handler; collects selected options by value
    const options = Array.from(e.target.selectedOptions).map((o) => o.value);
    setSelectedActivities(options);
  };

  // Toggle selection for a single activity (legacy support for button/checkbox UI)
  // Kept for potential reuse, not used by the dropdown UI.
  const toggleActivitySelection = (activity) => {
    setSelectedActivities((prev) => {
      const exists = prev.includes(activity);
      const next = exists ? prev.filter((a) => a !== activity) : [...prev, activity];
      return next;
    });
    setIsConfirmed(false);
    setConfirmedSequence(null);
    setSaveResponse(null);
  };

  const handleDragStart = (e, activity, fromSlot = null) => {
    setDragged({ activity, fromSlot });
    // for visual feedback via dataTransfer (not strictly necessary)
    e.dataTransfer.setData("text/plain", activity);
  };

  const handleDrop = (e, toSlotIndex) => {
    e.preventDefault();
    if (!dragged.activity && !e.dataTransfer) return;
    const activity = dragged.activity || e.dataTransfer.getData("text/plain");
    if (!activity) return;

    setSequence((prev) => {
      const next = prev.map((slot) => [...slot]);

      // Remove from previous slot if exists
      if (dragged.fromSlot !== null && next[dragged.fromSlot]) {
        next[dragged.fromSlot] = next[dragged.fromSlot].filter((a) => a !== activity);
      }

      // Ensure activity not present in any slot (unique placement)
      next.forEach((slot, idx) => {
        next[idx] = slot.filter((a) => a !== activity);
      });

      // Add to target slot if not already there
      if (!next[toSlotIndex].includes(activity)) {
        next[toSlotIndex].push(activity);
      }
      return next;
    });
    setDragged({ activity: null, fromSlot: null });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFromSlot = (slotIndex, activity) => {
    setSequence((prev) => {
      const next = prev.map((slot) => slot.filter((a) => a !== activity));
      return next;
    });
    setIsConfirmed(false);
  };

  const handleConfirm = async () => {
    // Allow partial submissions: at least one activity should be placed
    const placedCount = Array.from(placedSet).length;
    if (placedCount === 0) {
      setError("Place at least one activity into the sequence before confirming.");
      return;
    }

    setError(null);
    setIsConfirmed(true);
    setConfirmedSequence(sequence);

    // Send to backend
    try {
      const resp = await activitySequenceApi.saveSequence(selectedObjectType, sequence);
      setSaveResponse(resp);
      console.log("Saved sequence response:", resp);
    } catch (e) {
      setError(e.message || "Failed to save sequence");
      setIsConfirmed(false);
    }
  };

  return {
    objectTypes,
    selectedObjectType,
    activities,
    selectedActivities,
    availableTiles,
    sequence,
    isConfirmed,
    confirmedSequence,
    saveResponse,
    error,
    handleObjectTypeChange,
    handleActivitiesChange,
    handleDragStart,
    handleDrop,
    handleDragOver,
    removeFromSlot,
    handleConfirm,
    toggleActivitySelection,
  };
}
